import { Modal, Upload } from "@appkit4/react-components";
import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { getSelectedPeriod } from "../../services/GroupContext";
import CustomTooltipIcon from "../customTooltipIcon/customTooltipIcon";
import toast from "../DismissibleToast";
import { Button } from "../ReadonlyAwareInputs";
import ConfirmationModal from "./ConfirmationModal";
import "./DataImportModal.scss";
import { getDataImportHandlers } from "./utils/DataImportUtils";
import { downloadAllGroupDataImportTemplates } from "./utils/groupDataImportUtils";

const DataImportModal = ({
  pageName,
  data,
  setData,
  isPageLoading,
  onSuccess = () => {},
}) => {
  const isClientFacing = useSelector((state) => state.usageInfo.isClientFacing);
  const handlers = getDataImportHandlers(pageName);
  const [file, setFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isConfirmModalVisible, setIsConfirmModalVisible] = useState(false);
  const [newData, setNewData] = useState(null);
  const [warnings, setWarnings] = useState([]);
  const [errors, setErrors] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const uploadRef = useRef(null);
  const currency = getSelectedPeriod()?.currency || "GBP";
  const fxRate = getSelectedPeriod()?.fx_rate || "1";
  const isGroupDataModal = ["ngie", "angie", "qngie", "ebitda"].includes(
    pageName,
  );
  const displayNote =
    isGroupDataModal && (currency !== "GBP" || String(fxRate) !== "1");

  useEffect(() => {
    if (warnings?.length > 0) {
      setIsConfirmModalVisible(true);
    }
  }, [warnings]);

  const handleClearFiles = () => {
    const uploadElement = uploadRef.current;
    const deleteAllAttachmentsButton = uploadElement?.querySelector(
      ".ap-fileupload-footer-delete-attachment span",
    );

    if (uploadElement && deleteAllAttachmentsButton) {
      deleteAllAttachmentsButton.click();
    }
  };

  const uploadFile = (fileList) => {
    setErrors(null);
    setFile(fileList);
  };

  const showNotification = () => {
    toast.notification(
      `Imported Data won't be Saved until you click Save or Next Page`,
      { duration: 10000 },
    );
  };

  const handleImport = async () => {
    const handleDataImport = handlers["importHandler"];
    await handleDataImport({
      file,
      data,
      setData,
      setIsLoading,
      setNewData,
      setWarnings,
      setErrors,
      setIsModalVisible,
      setIsConfirmModalVisible,
      onSuccess,
    }).then((importedData) => {
      if (importedData) {
        handleClose();
        showNotification();
        // for Group Data imports
        onSuccess?.(importedData);
      }
    });
  };

  const handleExport = async () => {
    try {
      setIsLoading(true);
      await new Promise((resolve) => setTimeout(resolve, 0));
      const handleDataExport = handlers["templateDownloadHandler"];
      await handleDataExport({ data, isClientFacing });
    } catch (error) {
      console.error("Error downloading template:", error);
    } finally {
      setIsLoading(false)
    }
  };

  const handleGroupDataExport = async () => {
    try {
      setIsLoading(true);
      await new Promise((resolve) => setTimeout(resolve, 0));
      await downloadAllGroupDataImportTemplates({ isClientFacing });
    } catch (error) {
      console.error("Error downloading group data templates:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setErrors(null);
    handleClearFiles();
    setIsModalVisible(false);
    closeConfirmationModal();
    setIsLoading(false);
  };

  const showImportDataModal = () => {
    setIsModalVisible(true);
  };

  const handleRemove = () => {
    setFile(null);
    setErrors(null);
  };

  const handleConfirmation = () => {
    const handleConfirmationOnWarnings = handlers["confirmationHandler"];

    const importedData = handleConfirmationOnWarnings({
      setData: () => setData(newData),
      reset: () => {
        setFile(null);
        setWarnings(null);
        handleClearFiles();
        setIsConfirmModalVisible(false);
        setIsModalVisible(false);
      },
      showNotification: () => showNotification(),
      newData,
    });
    if (importedData) {
      handleClose();
      showNotification();
      onSuccess?.(importedData);
    }
  };

  const closeConfirmationModal = () => {
    setWarnings(null);
    setIsConfirmModalVisible(false);
  };

  return (
    <>
      <Button onClick={showImportDataModal} loading={isPageLoading}>
        Import/Export Template
      </Button>
      <Modal
        initialFocus={false}
        wrapperStyle={{ zIndex: "5000" }}
        header={
          <div className="space">
            <span className="Appkit4-icon icon-upload-outline large"></span>
            <p className="large no-margin">
              Import {handlers["pageName"]} Data
            </p>
            <CustomTooltipIcon
              tooltipText={`Use this function to download an excel template that can be populated with the data for the ${handlers["pageName"]}. Once complete, use the import function to upload the data so it is reflected on this page`}
            />
          </div>
        }
        ariaLabel={"Data Import Export"}
        onCancel={handleClose}
        visible={isModalVisible}
        modalStyle={{ width: "40rem" }}
        footer={
          <>
            <Button onClick={handleClose} kind="secondary">
              Cancel
            </Button>
            <div className="space">
              <Button
                kind="primary"
                onClick={() => handleImport()}
                disabled={!file || errors}
                loading={isLoading}
              >
                Import Data
              </Button>
            </div>
          </>
        }
      >
        <ConfirmationModal
          visible={isConfirmModalVisible}
          warnings={warnings}
          onClick={handleConfirmation}
          onCancel={closeConfirmationModal}
        />
        <p className="bold-text">Download</p>
        <p>
          Press "Download Template" below to download the {handlers["pageName"]}{" "}
          excel template
        </p>
        <p
          style={{
            fontStyle: "italic",
            fontSize: "0.9rem",
            border: "dotted 1px grey",
            borderRadius: "5px",
            padding: "5px",
            marginTop: "5px",
            textAlign: "center",
          }}
        >
          {displayNote &&
            `Inputs will be recorded in ${currency}. Any changes to the currency or fx rate need to be made ${pageName === "ngie" ? "on the page" : "in the NGIE section"}.`}
          {displayNote && <br />}
          Note: In order for changes made in the Analyser to be reflected in the
          template, it is first necessary to press the "Save button" on the main
          page.
        </p>
        <br />
        <div className="align-right">
          {isGroupDataModal && (
            <Button
              kind="primary"
              onClick={() => handleGroupDataExport()}
              loading={isLoading}
            >
              Download All Group Data Templates
            </Button>
          )}
          <Button
            kind="primary"
            onClick={() => handleExport()}
            loading={isLoading}
          >
            {isGroupDataModal
              ? `Donwnload ${handlers?.pageName} Template`
              : "Download Template"}
          </Button>
        </div>
        <br />
        <hr style={{ width: "100%", borderTop: "1px solid #ccc" }} />
        <br />
        <Upload
          ref={uploadRef}
          multiple={false}
          autoUpload={true}
          onSuccess={uploadFile}
          acceptFileType=".xlsx"
          uploadInstruction={`Attach the completed ${handlers["pageName"]} template, and then press the "Import Data" button below to populate the ${handlers["pageName"]} page`}
          uploadTitle="Import Data"
          maxFileSize={10 * 1024 * 1024}
          onRemove={handleRemove}
        ></Upload>
        {errors && (
          <>
            <br />
            <br />
            <hr style={{ width: "100%", borderTop: "1px solid #ccc" }} />
            <br />
            <h1 style={{ color: "red" }}>The following errors occurred:</h1>
            {errors.map((error, index) => (
              <div style={{ width: "100%" }}>
                <p className="bold-text">{error.title}</p>
                {error?.messages?.map((message) => (
                  <p className="red-text">{message}</p>
                ))}
                {index !== errors.length - 1 && (
                  <hr style={{ width: "100%", borderTop: "1px solid #ccc" }} />
                )}
              </div>
            ))}
          </>
        )}
      </Modal>
    </>
  );
};

export default DataImportModal;
